import React, { useEffect, useState } from 'react';
import { HiCheck, HiCash, HiCurrencyDollar, HiArrowUp, HiArrowDown } from 'react-icons/hi';
import apiClient from '../api/client';

export default function BudgetPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [budgetData, setBudgetData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ total_budget: 0 });
  const [loading, setLoading] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [settings, setSettings] = useState({
    optimization_metric: 'acos',
    target_acos: 30,
    target_cpo: 100,
    min_daily_limit: 300,
    max_daily_limit: 50000,
    lookback_days: 7,
    auto_distribute: true,
    auto_deposit: true,
  });
  const [month, setMonth] = useState('');

  useEffect(() => {
    loadClients();
    // Устанавливаем текущий месяц по умолчанию
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setMonth(currentMonth);
  }, []);

  useEffect(() => {
    if (selectedClient && month) {
      loadBudget();
    }
  }, [selectedClient, month]);

  const loadClients = async () => {
    try {
      const { data } = await apiClient.get('/clients');
      setClients(data || []);
      if (!selectedClient && Array.isArray(data) && data.length) {
        setSelectedClient(data[0].id);
      }
    } catch (error) {
      console.error('Ошибка загрузки клиентов:', error);
      setError('Не удалось загрузить клиентов');
    }
  };

  const loadBudget = async () => {
    setError('');
    setSuccess('');
    try {
      const { data } = await apiClient.get(`/budget/${selectedClient}?month=${month}`);
      setBudgetData(data);
      if (data.budget) {
        setForm({ total_budget: data.budget.total_budget || 0 });
        setSettings({
          optimization_metric: data.budget.optimization_metric || 'acos',
          target_acos: data.budget.target_acos !== null && data.budget.target_acos !== undefined ? data.budget.target_acos : 30,
          target_cpo: data.budget.target_cpo !== null && data.budget.target_cpo !== undefined ? data.budget.target_cpo : 100,
          min_daily_limit: data.budget.min_daily_limit !== null && data.budget.min_daily_limit !== undefined ? data.budget.min_daily_limit : 300,
          max_daily_limit: data.budget.max_daily_limit !== null && data.budget.max_daily_limit !== undefined ? data.budget.max_daily_limit : 50000,
          lookback_days: data.budget.lookback_days !== null && data.budget.lookback_days !== undefined ? data.budget.lookback_days : 7,
          auto_distribute: data.budget.auto_distribute !== undefined ? data.budget.auto_distribute : true,
          auto_deposit: data.budget.auto_deposit !== undefined ? data.budget.auto_deposit : true,
        });
      } else {
        setForm({ total_budget: 0 });
      }
    } catch (err) {
      console.error('Ошибка бюджета:', err);
      setError('Не удалось загрузить бюджет');
    }
  };

  const handleSetBudget = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    if (Number(form.total_budget) <= 0) {
      setError('Бюджет должен быть положительным');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data: postData } = await apiClient.post('/budget', {
        client_id: selectedClient,
        month: month,
        total_budget: Number(form.total_budget),
      });
      const distributionPending = Boolean(postData?.distribution_pending);
      setSuccess(
        distributionPending
          ? 'Бюджет сохранён. Распределение по кампаниям выполняется в фоне…'
          : 'Бюджет сохранён',
      );
      loadBudget();
    } catch (err) {
      console.error('Ошибка сохранения бюджета:', err);
      setError(err.response?.data?.message || 'Не удалось сохранить бюджет');
    } finally {
      setLoading(false);
    }
  };


  const handleSaveSettings = async () => {
    if (!selectedClient || !budgetData?.budget) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data: putData } = await apiClient.put(`/budget/${selectedClient}/settings`, {
        month: month,
        ...settings,
      });

      const distributionPending = Boolean(putData?.distribution_pending);
      setSuccess(
        distributionPending
          ? 'Настройки сохранены. Распределение бюджета выполняется в фоне…'
          : 'Настройки сохранены',
      );

      loadBudget();
    } catch (err) {
      console.error('Ошибка сохранения настроек:', err);
      setError(err.response?.data?.message || 'Не удалось сохранить настройки');
    } finally {
      setLoading(false);
    }
  };

  const getGroupColor = (group) => {
    switch (group) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'B': return 'bg-yellow-100 text-yellow-800';
      case 'C': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600';
      case 'paused': return 'text-yellow-600';
      case 'completed': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const remainingBudget = budgetData?.budget 
    ? (budgetData.budget.total_budget - budgetData.budget.spent_budget)
    : 0;
  const budgetUsage = budgetData?.budget?.total_budget > 0
    ? (budgetData.budget.spent_budget / budgetData.budget.total_budget) * 100
    : 0;

  return (
    <div className="relative z-10 max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Управление бюджетом</h2>
        <div className="flex gap-4 items-center">
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Месяц</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="glass-input px-3 py-2"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Клиент</label>
            <select
              className="glass-input px-3 py-2 min-w-[200px]"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              <option value="">-- Выберите клиента --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name || `Клиент ${client.id}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Сообщения об ошибках и успехе */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Основная информация о бюджете */}
      {budgetData && budgetData.budget && (
        <>
          <div className="grid grid-cols-4 gap-3">
            {/* Общий бюджет */}
            <div className="glass-card p-4 card-hover">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Общий бюджет</p>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <HiCurrencyDollar className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <form onSubmit={handleSetBudget} className="flex items-center gap-2 mb-1">
                <input
                  type="number"
                  value={form.total_budget}
                  onChange={(e) => setForm({ total_budget: e.target.value })}
                  className="text-lg font-bold text-blue-600 border-0 border-b-2 border-blue-300 focus:border-blue-600 focus:outline-none w-32"
                  min="0"
                  step="100"
                  required
                />
                <span className="text-lg font-bold text-blue-600">₽</span>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  title="Сохранить бюджет"
                >
                  {loading ? '...' : <HiCheck className="w-3 h-3" />}
                </button>
              </form>
            </div>

            {/* Потрачено */}
            <div className="glass-card p-4 card-hover">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Потрачено</p>
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <HiCash className="w-4 h-4 text-orange-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {budgetData.budget.spent_budget?.toLocaleString('ru-RU')} ₽
              </p>
            </div>

            {/* Остаток */}
            <div className="glass-card p-4 card-hover">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Остаток</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  remainingBudget < 0 ? 'bg-red-100' : 'bg-green-100'
                }`}>
                  {remainingBudget < 0 ? (
                    <HiArrowDown className={`w-4 h-4 ${remainingBudget < 0 ? 'text-red-600' : 'text-green-600'}`} />
                  ) : (
                    <HiArrowUp className={`w-4 h-4 ${remainingBudget < 0 ? 'text-red-600' : 'text-green-600'}`} />
                  )}
                </div>
              </div>
              <p className={`text-lg font-bold ${remainingBudget < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingBudget.toLocaleString('ru-RU')} ₽
              </p>
            </div>

            {/* Использовано */}
            <div className="glass-card p-4 card-hover">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Использовано</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  budgetUsage > 100 ? 'bg-red-100' :
                  budgetUsage > 80 ? 'bg-orange-100' :
                  'bg-green-100'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${
                    budgetUsage > 100 ? 'bg-red-600' :
                    budgetUsage > 80 ? 'bg-orange-600' :
                    'bg-green-600'
                  }`} />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1">{budgetUsage.toFixed(1)}%</p>
              <div className="flex-1 bg-gray-200 rounded-full h-2 mt-1">
                <div
                  className={`h-2 rounded-full ${
                    budgetUsage > 100 ? 'bg-red-500' :
                    budgetUsage > 80 ? 'bg-orange-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, budgetUsage)}%` }}
                />
              </div>
            </div>
          </div>

          {settings.auto_distribute && (
            <div className="glass-card p-4 mt-3">
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <HiCheck className="w-5 h-5 text-green-600" />
                <span>Автоматическое распределение включено</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Настройки автоматического распределения */}
      {budgetData?.budget && (
        <div className="glass-card p-6 space-y-6">
          <h3 className="text-lg font-semibold">Настройки автоматического распределения</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Метрика оптимизации</label>
                <select
                  value={settings.optimization_metric}
                  onChange={(e) => setSettings({ ...settings, optimization_metric: e.target.value })}
                  className="glass-input w-full"
                >
                  <option value="acos">ACoS (Advertising Cost of Sales)</option>
                  <option value="cpo">CPO (Cost Per Order)</option>
                </select>
              </div>

              {settings.optimization_metric === 'acos' ? (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Целевой ACoS (%)
                  </label>
                  <input
                    type="number"
                    value={settings.target_acos}
                    onChange={(e) => setSettings({ ...settings, target_acos: Number(e.target.value) })}
                    className="glass-input w-full"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Целевой CPO (₽)
                  </label>
                  <input
                    type="number"
                    value={settings.target_cpo}
                    onChange={(e) => setSettings({ ...settings, target_cpo: Number(e.target.value) })}
                    className="glass-input w-full"
                    min="0"
                    step="1"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  Дней для анализа статистики
                </label>
                <input
                  type="number"
                  value={settings.lookback_days}
                  onChange={(e) => setSettings({ ...settings, lookback_days: Number(e.target.value) })}
                  className="glass-input w-full"
                  min="1"
                  max="30"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Минимальный дневной лимит (₽)
                </label>
                <input
                  type="number"
                  value={settings.min_daily_limit}
                  onChange={(e) => setSettings({ ...settings, min_daily_limit: Number(e.target.value) })}
                  className="glass-input w-full"
                  min="0"
                  step="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Максимальный дневной лимит (₽)
                </label>
                <input
                  type="number"
                  value={settings.max_daily_limit}
                  onChange={(e) => setSettings({ ...settings, max_daily_limit: Number(e.target.value) })}
                  className="glass-input w-full"
                  min="0"
                  step="1000"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_distribute}
                    onChange={(e) => setSettings({ ...settings, auto_distribute: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Автоматическое распределение</span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  Бюджет будет автоматически распределяться при сохранении бюджета или настроек, а также по расписанию (ежедневно)
                </p>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_deposit}
                    onChange={(e) => setSettings({ ...settings, auto_deposit: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Автоматическое пополнение</span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  Бюджеты кампаний будут автоматически пополняться при исчерпании
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>
        </div>
      )}

      {/* Установка бюджета (только если бюджет не установлен) */}
      {(!budgetData || !budgetData.budget) && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4">Установить месячный бюджет</h3>
          <form onSubmit={handleSetBudget} className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Месячный бюджет (₽)</label>
              <input
                type="number"
                value={form.total_budget}
                onChange={(e) => setForm({ total_budget: e.target.value })}
                className="glass-input w-full"
                min="0"
                step="100"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </form>
        </div>
      )}

      {/* Таблица кампаний */}
      {budgetData && budgetData.campaigns && budgetData.campaigns.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4">Кампании</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-3 px-2">Кампания</th>
                  <th className="py-3 px-2">Статус</th>
                  <th className="py-3 px-2">Дневной лимит</th>
                  <th className="py-3 px-2">Расход</th>
                  <th className="py-3 px-2">Выручка</th>
                  <th className="py-3 px-2">Заказы</th>
                  <th className="py-3 px-2">ACoS</th>
                  <th className="py-3 px-2">CPO</th>
                  <th className="py-3 px-2">Остаток, шт</th>
                  <th className="py-3 px-2" title="(остаток/(остаток+15))^1.35 — доля в распределении бюджета">Коэф. остаток</th>
                  <th className="py-3 px-2">Группа</th>
                </tr>
              </thead>
              <tbody>
                {budgetData.campaigns.map((campaign) => (
                  <tr key={campaign.advert_id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">
                      {campaign.name || `Кампания ${campaign.advert_id}`}
                    </td>
                    <td className="py-3 px-2">
                      <span className={`font-medium ${getStatusColor(campaign.status === 9 ? 'active' : 'paused')}`}>
                        {campaign.status === 9 ? 'Активна' : campaign.status === 11 ? 'На паузе' : 'Неизвестно'}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      {campaign.daily_limit 
                        ? `${campaign.daily_limit.toLocaleString('ru-RU')} ₽`
                        : '—'
                      }
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stats?.spend?.toLocaleString('ru-RU') || '0'} ₽
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stats?.revenue?.toLocaleString('ru-RU') || '0'} ₽
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stats?.orders || 0}
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stats?.acos !== null && campaign.stats?.acos !== undefined
                        ? `${campaign.stats.acos.toFixed(2)}%`
                        : '—'
                      }
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stats?.cpo !== null && campaign.stats?.cpo !== undefined
                        ? `${campaign.stats.cpo.toFixed(2)} ₽`
                        : '—'
                      }
                    </td>
                    <td className="py-3 px-2">
                      {campaign.promoted_stock_remain != null
                        ? Number(campaign.promoted_stock_remain).toLocaleString('ru-RU')
                        : '—'}
                    </td>
                    <td className="py-3 px-2">
                      {campaign.stock_factor != null ? (
                        <span
                          className={
                            campaign.stock_factor === 0
                              ? 'text-red-600 font-medium'
                              : campaign.stock_factor < 0.15
                              ? 'text-orange-600 font-medium'
                              : 'text-gray-800'
                          }
                        >
                          {Math.round(campaign.stock_factor * 100)}%
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-3 px-2">
                      {campaign.group ? (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getGroupColor(campaign.group)}`}>
                          {campaign.group}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {budgetData && (!budgetData.campaigns || budgetData.campaigns.length === 0) && (
        <div className="glass-card p-6 text-center text-gray-500">
          Нет кампаний для отображения
        </div>
      )}
    </div>
  );
}
